import { useState, useEffect, useCallback } from 'react';
import { detectPWAPlatform, isInSafari, isPWAStandalone } from '@/lib/pwaPlatform';

export function usePWAInstall() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  // iOS Safari never fires `beforeinstallprompt`, so it's never `isInstallable`.
  // This flags it separately so callers can offer manual instructions instead.
  const [isIOSSafari, setIsIOSSafari] = useState(false);

  useEffect(() => {
    setIsInstalled(isPWAStandalone());
    setIsIOSSafari(detectPWAPlatform() === 'ios' && isInSafari());

    const handleBeforeInstallPrompt = (e: BeforeInstallPromptEvent) => {
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();
      // Save the event so it can be triggered later
      setDeferredPrompt(e);
      setIsInstallable(true);
    };

    const handleAppInstalled = () => {
      setIsInstalled(true);
      setIsInstallable(false);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (!deferredPrompt) {
      return { success: false, outcome: 'no-prompt' as const };
    }

    try {
      // Show the install prompt
      await deferredPrompt.prompt();
      
      // Wait for the user to respond
      const { outcome } = await deferredPrompt.userChoice;
      
      // Clear the deferred prompt
      setDeferredPrompt(null);
      setIsInstallable(false);

      return { success: outcome === 'accepted', outcome };
    } catch (error) {
      console.error('Error prompting install:', error);
      return { success: false, outcome: 'error' as const };
    }
  }, [deferredPrompt]);

  return {
    isInstallable,
    isInstalled,
    isIOSSafari,
    promptInstall,
  };
}
